import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBriefcase, FiUsers, FiMail, FiPhone, FiBookOpen, FiChevronRight, FiMapPin } from 'react-icons/fi';
import api from '../../api/config';
import './gmincele.css'; // Ortak kullanılan kurumsal CSS dosyası

const YMIncele = () => {
  const [activeTab, setActiveTab] = useState('yurt-personel');
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const tabs = [
    { key: 'yurt-personel', label: 'Yurt Personel' },
    { key: 'yurt-yonetici', label: 'Yurt Yönetici' },
  ];

  useEffect(() => {
    fetchJobs(activeTab);
  }, [activeTab]);

  const fetchJobs = async (type) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/basvurular/tum/${type}`);
      const data = response.data;

      if (!data || !data.ilanlar) {
        setJobs([]);
        setSelectedJob(null);
        return;
      }

      const jobsWithCV = await Promise.all((data.ilanlar || []).map(async (job) => {
        const basvuranlarWithCV = await Promise.all((job.basvuranlar || []).map(async (applicant) => {
          let cvData = null;
          try {
            const cvResponse = await api.get(`/api/cv/${applicant.user_id}`);
            cvData = cvResponse.data?.[0] || null;
          } catch (cvErr) {
            console.error(`CV fetch error for user ${applicant.user_id}:`, cvErr);
          }
          return { ...applicant, cv: cvData };
        }));
        return { ...job, basvuranlar: basvuranlarWithCV };
      }));

      setJobs(jobsWithCV);
      
      // Sekme değiştiğinde ilk ilanı otomatik seçili getir
      if (jobsWithCV.length > 0) {
        setSelectedJob(jobsWithCV[0]);
      } else {
        setSelectedJob(null);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setError('Veriler yüklenirken bir sorun oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplicantClick = (applicant) => {
    navigate(`/basvuru-detay/${applicant.user_id}`, {
      state: { applicantData: applicant, kaynakTab: 'yurtMudurlukleriBasvuru' }
    });
  };

  if (loading) {
    return (
      <div className="corporate-loader-container">
        <div className="corp-loader"></div>
        <span>Veriler Yükleniyor...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="corp-dashboard">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="corp-dashboard">
      {/* Üst Başlık Alanı */}
      <div className="corp-header-bar">
        <div>
          <h1>Yurt Başvuru Paneli</h1>
          <p className="subtitle">Yurt Personel ve Yönetici Kadrosu İK İzleme</p>
        </div>
      </div>

      {/* Kurumsal Sekme Yönetimi (Tab-Bar) */}
      <div className="corp-tabs-wrapper" style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              border: '1px solid',
              borderColor: activeTab === tab.key ? '#D12A2C' : '#cbd5e1',
              backgroundColor: activeTab === tab.key ? '#D12A2C' : '#ffffff',
              color: activeTab === tab.key ? '#ffffff' : '#475569',
              transition: 'all 0.2s ease'
            }}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* İlan Seçim Dropdown Alanı */}
      <div className="corp-filter-section">
        <label htmlFor="job-select"><FiBriefcase /> Aktif İlan Seçimi:</label>
        <select 
          id="job-select"
          value={selectedJob?.ilan_detay?.id || ''} 
          onChange={(e) => {
            const job = jobs.find(j => j.ilan_detay.id === parseInt(e.target.value));
            setSelectedJob(job);
          }}
          disabled={jobs.length === 0}
        >
          {jobs.length === 0 ? (
            <option>Açık ilan bulunmamaktadır</option>
          ) : (
            jobs.map(job => (
              <option key={job.ilan_detay.id} value={job.ilan_detay.id}>
                {job.ilan_detay.ilan_basligi} ({job.basvuranlar?.length || 0} Başvuru)
              </option>
            ))
          )}
        </select>
      </div>

      {/* Başvuru Listesi Panel Alanı */}
      <div className="corp-content-card">
        {selectedJob ? (
          <>
            <div className="panel-meta">
              <div className="meta-title">
                <h2>{selectedJob.ilan_detay.ilan_basligi}</h2>
                <span className="corp-badge bg-gray">{selectedJob.ilan_detay.is_tipi || 'Tam Zamanlı'}</span>
              </div>
              <div className="meta-stats">
                <FiUsers /> <span><strong>{selectedJob.basvuranlar?.length || 0}</strong> Toplam Aday</span>
              </div>
            </div>

            {!selectedJob.basvuranlar || selectedJob.basvuranlar.length === 0 ? (
              <div className="corp-empty-state">Bu ilana henüz herhangi bir başvuru yapılmamıştır.</div>
            ) : (
              <div className="corp-list">
                {/* Tablo Sütun Başlıkları */}
                <div className="corp-list-header">
                  <div className="col-name">Aday Adı Soyadı</div>
                  <div className="col-contact">İletişim Bilgileri</div>
                  <div className="col-edu">Eğitim Durumu</div>
                  <div className="col-exp">İkamet / Adres</div>
                  <div className="col-action"></div>
                </div>

                {/* Aday Satırları */}
                {selectedJob.basvuranlar.map((applicant) => (
                  <div 
                    key={applicant.user_id} 
                    className="corp-list-item"
                    onClick={() => handleApplicantClick(applicant)}
                  >
                    <div className="col-name font-semibold">
                      {applicant.ad} {applicant.soyad}
                    </div>
                    
                    <div className="col-contact contact-details">
                      <span><FiMail /> {applicant.email}</span>
                      <span><FiPhone /> {applicant.cep_telefonu}</span>
                    </div>
                    
                    <div className="col-edu">
                      <span className="corp-tag-edu">
                        <FiBookOpen /> {applicant.mezuniyet || '-'}
                      </span>
                    </div>
                    
                    <div className="col-exp truncate-text text-muted" style={{ fontSize: '0.85rem' }}>
                      <FiMapPin style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                      {applicant.ev_adresi || 'Adres Belirtilmemiş'}
                    </div>
                    
                    <div className="col-action">
                      <button className="corp-view-btn">
                        İncele <FiChevronRight />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="corp-empty-state">Bu kategoriye ait listelenecek ilan bulunamadı.</div>
        )}
      </div>
    </div>
  );
};

export default YMIncele;